import {useTherapySequenceActions} from '@/modules/patients-therapies/hooks'
import {useGetRestVials} from '@/modules/stocks/rest-vials/api'
import {closeSpoilageDialog} from './util'
import {
  Button,
  ButtonGroup,
  Box,
  CheckboxCard,
  CheckboxGroup,
  Fieldset,
  HStack,
  RadioCard,
  Text,
  VStack,
} from '@chakra-ui/react'
import {RestVialStatus} from '@cypro/engine/database/schema'
import {QaRejectReason} from '@cypro/engine/mutators'
import {PatientTherapySequence, RestVial} from '@cypro/engine/schema'
import {DialogHeader, DialogBody, DialogFooter} from '@cypro/ui/components'
import {useAppForm} from '@cypro/ui/integrations/form'
import {getExpiryDateTimeFormatter, getNumberFormatter} from '@cypro/ui/utils'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {LuRefreshCw, LuCircleX} from 'react-icons/lu'
import * as z from 'zod'

export interface SpoilageDialogProps {
  sequence: PatientTherapySequence
}

// no reason is preselected, so the pharmacist must choose one (CY-340)
const SpoilageFormSchema = z
  .object({
    remake: z.boolean(),
    reason: z.string(),
    note: z.string(),
    restVialIds: z.array(z.string()),
  })
  .superRefine((value, ctx) => {
    if (!QaRejectReason.safeParse(value.reason).success) {
      ctx.addIssue({code: 'custom', path: ['reason'], message: 'reason_required'})
    }
    if (value.reason === QaRejectReason.enum.OTHER && !value.note.trim()) {
      ctx.addIssue({code: 'custom', path: ['note'], message: 'notes_required'})
    }
  })

export function SpoilageDialog({sequence}: SpoilageDialogProps) {
  const {t} = useTranslate('common')
  const {sendEvent} = useTherapySequenceActions(sequence)
  // only vials still in stock: an In use or Used rest vial belongs to another preparation and is never discarded here (CY-329)
  const {restVials} = useGetRestVials({
    filters: {therapySequences: [sequence.id], status: [RestVialStatus.enum.AVAILABLE]},
    sort: {dir: 'asc', field: 'createdAt'},
  })

  const form = useAppForm({
    defaultValues: {
      remake: false,
      reason: '',
      note: '',
      restVialIds: [] as string[],
    },
    validators: {
      onDynamic: SpoilageFormSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmit: async ({value}) => {
      const reason = value.reason as QaRejectReason
      // send only ticks still on screen: a vial taken by another preparation drops out of the live list
      const listedIds = new Set(restVials.map(r => r.id))
      await sendEvent({
        id: sequence.id,
        event: {
          type: 'SPOILAGE',
          body: {
            remake: value.remake,
            reason,
            note: reason === QaRejectReason.enum.OTHER ? value.note.trim() : undefined,
            restVialIds: value.restVialIds.filter(id => listedIds.has(id)),
          },
        },
      })
      closeSpoilageDialog()
    },
  })

  const reasonItems = QaRejectReason.options.map(reason => ({label: t(`qa_reject_reason.${reason}`), value: reason}))
  const isOtherReason = useStore(form.store, state => state.values.reason === QaRejectReason.enum.OTHER)

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader description={t('spoilage_dialog.description')}>{t('spoilage_dialog.title')}</DialogHeader>

        <DialogBody minHeight={'none'} maxWidth={'100%'}>
          <form.AppField name="remake" mode="value">
            {field => (
              <RadioCard.Root
                value={field.state.value ? 'remake' : 'cancel'}
                onValueChange={e => field.handleChange(e.value === 'remake')}
              >
                <VStack align="stretch">
                  <RadioCard.Item value="cancel">
                    <RadioCard.ItemHiddenInput />
                    <RadioCard.ItemControl>
                      <Box
                        display={'flex'}
                        flexDirection={'row'}
                        justifyContent={'flex-start'}
                        gap={2}
                        padding={2}
                        width={'100%'}
                      >
                        <LuCircleX size={24} />
                        <Box>
                          <Text fontWeight={'medium'} fontSize={'sm'}>
                            {t('spoilage_dialog.cancel_order.title')}
                          </Text>
                          <Text fontWeight={'normal'} fontSize={'sm'} color="fg.muted">
                            {t('spoilage_dialog.cancel_order.description')}
                          </Text>
                        </Box>
                      </Box>
                      <RadioCard.ItemIndicator />
                    </RadioCard.ItemControl>
                  </RadioCard.Item>
                  <RadioCard.Item value="remake">
                    <RadioCard.ItemHiddenInput />
                    <RadioCard.ItemControl>
                      <Box
                        display={'flex'}
                        flexDirection={'row'}
                        justifyContent={'flex-start'}
                        gap={2}
                        padding={2}
                        width={'100%'}
                      >
                        <LuRefreshCw size={24} />
                        <Box>
                          <Text fontWeight={'medium'} fontSize={'sm'}>
                            {t('spoilage_dialog.remake_order.title')}
                          </Text>
                          <Text fontWeight={'normal'} fontSize={'sm'} color="fg.muted">
                            {t('spoilage_dialog.remake_order.description')}
                          </Text>
                        </Box>
                      </Box>
                      <RadioCard.ItemIndicator />
                    </RadioCard.ItemControl>
                  </RadioCard.Item>
                </VStack>
              </RadioCard.Root>
            )}
          </form.AppField>

          <form.AppField name="reason" mode="value">
            {field => (
              <field.Select
                label={t('reason')}
                placeholder={t('select_reason', 'Select a reason')}
                items={reasonItems}
                required
                marginTop="4"
              />
            )}
          </form.AppField>

          {isOtherReason && (
            <Box marginTop="4">
              <form.AppField name="note">
                {field => <field.Textarea label={t('note')} rows={3} maxLength={500} required />}
              </form.AppField>
            </Box>
          )}

          <form.AppField name="restVialIds" mode="value">
            {field => (
              <Fieldset.Root marginTop="4">
                <Fieldset.Legend fontSize="sm" fontWeight="medium">
                  {t('spoilage_dialog.rest_vials.title')}
                </Fieldset.Legend>
                <Fieldset.HelperText>{t('spoilage_dialog.rest_vials.description')}</Fieldset.HelperText>
                {restVials.length ? (
                  <CheckboxGroup
                    name="restVialIds"
                    value={field.state.value.filter(id => restVials.some(r => r.id === id))}
                    onValueChange={changes => field.handleChange(changes)}
                  >
                    <VStack align="stretch" gap="2">
                      {restVials.map(restVial => (
                        <RestVialCheckboxCard key={restVial.id} restVial={restVial} />
                      ))}
                    </VStack>
                  </CheckboxGroup>
                ) : (
                  <Text fontSize="sm" color="fg.muted">
                    {t('spoilage_dialog.rest_vials.empty')}
                  </Text>
                )}
              </Fieldset.Root>
            )}
          </form.AppField>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" width="100%" justifyContent="flex-end">
            <Button variant={'outline'} colorPalette={'gray'} onClick={closeSpoilageDialog}>
              {t('cancel')}
            </Button>
            <form.SubmitButton colorPalette="red">{t('continue')}</form.SubmitButton>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}

function RestVialCheckboxCard({restVial}: {restVial: RestVial}) {
  const unit = restVial.tradeName?.strengthUnit?.ucumCode || 'mg'
  const amount = restVial.amount == null ? '-' : `${getNumberFormatter().format(Number(restVial.amount))}${unit}`
  const details = [
    amount,
    restVial.stockLocation?.name,
    restVial.expiryDate ? getExpiryDateTimeFormatter().format(new Date(restVial.expiryDate)) : null,
  ].filter(Boolean)

  return (
    <CheckboxCard.Root value={restVial.id} size="sm">
      <CheckboxCard.HiddenInput />
      <CheckboxCard.Control>
        <CheckboxCard.Content>
          <HStack gap="2">
            <Text fontWeight="medium" fontSize="sm">{`#${restVial.internalId}`}</Text>
            <Text fontSize="sm">{restVial.tradeName?.name || '-'}</Text>
          </HStack>
          <Text fontSize="sm" color="fg.muted">
            {details.join(' · ')}
          </Text>
        </CheckboxCard.Content>
        <CheckboxCard.Indicator />
      </CheckboxCard.Control>
    </CheckboxCard.Root>
  )
}
